import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';

import type { Field, Form, ValidatedFormValue } from './magicalFormsTypes';
import { touchAllFormFields } from './touchAllFormFields';

type FieldFromFieldInstance<TForm extends Form<Field>> = TForm['_field'];

export function useSubmit<TForm extends Form<Field>, TOnSubmitReturn>(
  form: TForm,
  onSubmit: (
    form: ValidatedFormValue<FieldFromFieldInstance<TForm>>
  ) => TOnSubmitReturn,
  options?: {
    disableScrollToFirstError?: boolean;
  }
) {
  const [submission, setSubmission] = useState<
    { target?: HTMLElement } | undefined
  >();

  useEffect(() => {
    if (!submission) {
      return;
    }
    const domNode = submission?.target
      ?.closest('form')
      ?.querySelector(`[aria-invalid=true]`);

    if (typeof domNode?.scrollIntoView === 'function') {
      domNode.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'start',
      });
    }
  }, [submission]);

  return (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    if (form.validity === 'valid') {
      return onSubmit(form.value);
    } else {
      touchAllFormFields(form as Form<Field>);
      if (!options?.disableScrollToFirstError) {
        setSubmission({ target: event?.currentTarget });
      }
    }
  };
}
